import type { PaginatedTransactions } from '@/types/employeeProfile';
import type {
  ClientListQuery,
  ClientTransactionsQuery,
  CreateOrganizationWithAdminRequest,
  CreateOrganizationWithAdminResponse,
  OrganizationDetail,
  OrganizationDocument,
  OrganizationEmployee,
  PaginatedClients,
  SuperAdminClientAnalytics,
} from '@/types/superAdminClients';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest, BASE_URL } from './base.service';

/**
 * Super Admin Clients Service
 * Handles all Super Admin client management API endpoints
 */

export const superAdminClientsService = {
  /**
   * Get client analytics summary (for ClientListPage header cards)
   */
  async getClientAnalytics(): Promise<SuperAdminClientAnalytics> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<SuperAdminClientAnalytics>(
      '/api/v1/analytics/super-admin/client-analytics',
      {},
      token
    );
  },

  /**
   * List all clients with pagination and filters
   */
  async listClients(query?: ClientListQuery): Promise<PaginatedClients> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);
    if (query?.status) searchParams.append('status', query.status);
    if (query?.clientType) searchParams.append('clientType', query.clientType);
    if (query?.search) searchParams.append('search', query.search);
    if (query?.registrationDateFrom)
      searchParams.append('registrationDateFrom', query.registrationDateFrom);
    if (query?.registrationDateTo)
      searchParams.append('registrationDateTo', query.registrationDateTo);

    const url = `/api/v1/organizations/super-admin/clients${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedClients>(url, {}, token);
  },

  /**
   * Get client detail by ID
   */
  async getClientDetail(clientId: string): Promise<OrganizationDetail> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<OrganizationDetail>(
      `/api/v1/organizations/super-admin/clients/${clientId}`,
      {},
      token
    );
  },

  /**
   * Get client employees
   */
  async getClientEmployees(clientId: string): Promise<OrganizationEmployee[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<OrganizationEmployee[]>(
      `/api/v1/organizations/super-admin/clients/${clientId}/employees`,
      {},
      token
    );
  },

  /**
   * Get client transactions (for Movimientos tab)
   * Uses the shared /transactions endpoint with organization_id filter
   */
  async getClientTransactions(query: ClientTransactionsQuery): Promise<PaginatedTransactions> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    searchParams.append('organization_id', query.organization_id);
    if (query.page) searchParams.append('page', query.page.toString());
    if (query.limit) searchParams.append('limit', query.limit.toString());
    if (query.orderDirection) searchParams.append('orderDirection', query.orderDirection);
    if (query.type) searchParams.append('type', query.type);
    if (query.status) searchParams.append('status', query.status);
    if (query.side) searchParams.append('side', query.side);
    if (query.user_id) searchParams.append('user_id', query.user_id);
    if (query.account_id) searchParams.append('account_id', query.account_id);

    return authenticatedRequest<PaginatedTransactions>(
      `/api/v1/transactions?${searchParams.toString()}`,
      {},
      token
    );
  },

  /**
   * Create organization with admin
   */
  async createOrganizationWithAdmin(
    data: CreateOrganizationWithAdminRequest
  ): Promise<CreateOrganizationWithAdminResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<CreateOrganizationWithAdminResponse>(
      '/api/v1/organizations/with-admin',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * List an organization's documents (free-form uploads + onboarding docs)
   */
  async listOrganizationDocuments(organizationId: string): Promise<OrganizationDocument[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<OrganizationDocument[]>(
      `/api/v1/organizations/${organizationId}/documents`,
      {},
      token
    );
  },

  /**
   * Upload one or more free-form documents for an organization (multipart)
   */
  async uploadOrganizationDocuments(
    organizationId: string,
    files: File[],
    documentType: string
  ): Promise<{ success: true; documents: OrganizationDocument[] }> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('documentType', documentType);

    // Do not use authenticatedRequest here: multipart needs the browser to set
    // the Content-Type (with boundary), which that helper forces to JSON.
    const response = await fetch(
      `${BASE_URL}/api/v1/organizations/${organizationId}/documents`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(errorData.message ?? 'Error al subir el documento', response.status);
    }

    return response.json() as Promise<{ success: true; documents: OrganizationDocument[] }>;
  },

  /**
   * Delete an organization document
   */
  async deleteOrganizationDocument(
    organizationId: string,
    documentId: string
  ): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    await authenticatedRequest<{ success: true }>(
      `/api/v1/organizations/${organizationId}/documents/${documentId}`,
      { method: 'DELETE' },
      token
    );
  },
};
